import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import api from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { toast } from "sonner";
import { Plus, CalendarDays, Trash2, Sparkles, Clock, ChevronDown, ChevronRight } from "lucide-react";

const iso = (d) => d.toISOString().slice(0, 10);
const MEETING_COLORS = [
  { c: "#DC2626", label: "Red — critical" },
  { c: "#D97706", label: "Amber — important" },
  { c: "#2563EB", label: "Blue" },
  { c: "#059669", label: "Green" },
  { c: "#7C3AED", label: "Violet" },
  { c: "#DB2777", label: "Pink" },
  { c: "#475569", label: "Slate" },
];
const fmtTime = (t) => {
  if (!t) return "";
  const [h, m] = t.split(":").map(Number);
  const ampm = h >= 12 ? "p" : "a";
  return `${((h + 11) % 12) + 1}${m ? `:${String(m).padStart(2, "0")}` : ""}${ampm}`;
};

export default function MeetingWeekRow({ projects, divider = true }) {
  const [meetings, setMeetings] = useState([]);
  const [dialog, setDialog] = useState(null); // {mode:"new", date} | {mode:"edit", meeting}
  const [open, setOpen] = useState(() => localStorage.getItem("dash-meetings-open") !== "0");
  const toggle = () => setOpen((o) => { localStorage.setItem("dash-meetings-open", o ? "0" : "1"); return !o; });
  const now = new Date();
  const monday = new Date(now);
  monday.setDate(now.getDate() - ((now.getDay() + 6) % 7));
  if (now.getDay() === 0 || now.getDay() === 6) monday.setDate(monday.getDate() + 7);
  const days = Array.from({ length: 5 }, (_, i) => { const d = new Date(monday); d.setDate(monday.getDate() + i); return d; });
  const todayIso = iso(now);
  const defaultDate = (todayIso >= iso(days[0]) && todayIso <= iso(days[4])) ? todayIso : iso(days[0]);

  const load = async () => {
    try {
      const { data } = await api.get(`/meetings?start=${iso(days[0])}&end=${iso(days[4])}`);
      setMeetings(data);
    } catch { /* silent */ }
  };
  useEffect(() => { load(); }, []); // eslint-disable-line react-hooks/exhaustive-deps

  return (
    <div className={divider ? "mt-3 pt-2.5 border-t border-border/70" : ""} data-testid="meeting-week-row">
      <div className="flex items-center justify-between gap-2 mb-1.5">
        <button onClick={toggle} className="text-[10px] font-bold uppercase tracking-widest text-muted-foreground hover:text-foreground transition-colors inline-flex items-center gap-1.5" data-testid="meeting-row-toggle">
          {open ? <ChevronDown className="h-3.5 w-3.5" /> : <ChevronRight className="h-3.5 w-3.5" />}
          <CalendarDays className="h-3 w-3" /> Meetings this week
          {!open && meetings.length > 0 && <span className="normal-case tracking-normal font-semibold text-muted-foreground/80">· {meetings.length} scheduled</span>}
        </button>
        <button onClick={() => setDialog({ mode: "new", date: defaultDate })} className="text-[11px] font-bold text-primary hover:underline inline-flex items-center gap-0.5" data-testid="meeting-schedule-btn">
          <Plus className="h-3 w-3" /> Schedule
        </button>
      </div>
      {open && <div className="grid grid-cols-5 gap-1.5" data-testid="meeting-day-grid">
        {days.map((d) => {
          const dIso = iso(d);
          const dayMeetings = meetings.filter((m) => m.date === dIso);
          const isToday = dIso === todayIso;
          const isPast = dIso < todayIso;
          return (
            <div key={dIso} className={`rounded-lg border px-1.5 py-1 min-h-[52px] group/day ${isToday ? "border-primary/50 bg-primary/5" : "border-border/70 bg-white"} ${isPast ? "opacity-80" : ""}`} data-testid={`meeting-day-${dIso}`}>
              <div className="flex items-center justify-between">
                <span className={`text-[9px] font-bold uppercase ${isToday ? "text-primary" : "text-muted-foreground"}`}>
                  {d.toLocaleDateString(undefined, { weekday: "short" })} {d.getDate()}
                </span>
                <button onClick={() => setDialog({ mode: "new", date: dIso })} title="Schedule a meeting this day"
                  className="opacity-0 group-hover/day:opacity-100 transition-opacity text-muted-foreground hover:text-primary" data-testid={`meeting-add-${dIso}`}>
                  <Plus className="h-3 w-3" />
                </button>
              </div>
              <div className="space-y-0.5 mt-0.5">
                {dayMeetings.slice(0, 3).map((m) => (
                  <button key={m.id} onClick={() => setDialog({ mode: "edit", meeting: m })}
                    className={`w-full text-left text-[10px] leading-tight px-1 py-0.5 rounded truncate font-semibold transition-all ${m.color ? "hover:brightness-90" : (isPast ? (m.recap ? "bg-emerald-50 text-emerald-800 hover:bg-emerald-100" : "bg-amber-50 text-amber-800 hover:bg-amber-100") : "bg-blue-50 text-blue-800 hover:bg-blue-100")}`}
                    style={m.color ? { background: `${m.color}1c`, color: m.color, boxShadow: `inset 2px 0 0 ${m.color}` } : undefined}
                    title={`${m.title}${m.time ? ` · ${m.time}` : ""}${isPast ? (m.recap ? " · recap saved" : " · add a recap") : ""}`}
                    data-testid={`meeting-chip-${m.id}`}>
                    {m.time ? <span className="font-bold">{fmtTime(m.time)} </span> : null}{m.title}
                  </button>
                ))}
                {dayMeetings.length > 3 && <p className="text-[9px] text-muted-foreground px-1">+{dayMeetings.length - 3} more</p>}
              </div>
            </div>
          );
        })}
      </div>}
      {dialog && <MeetingDialog dialog={dialog} projects={projects} todayIso={todayIso} onClose={() => setDialog(null)} onChanged={() => { setDialog(null); load(); }} />}
    </div>
  );
}

function MeetingDialog({ dialog, projects, todayIso, onClose, onChanged }) {
  const nav = useNavigate();
  const m = dialog.meeting;
  const [form, setForm] = useState({
    title: m?.title || "", date: m?.date || dialog.date, time: m?.time || "",
    project_id: m?.project_id || "", attendees: (m?.attendees || []).join(", "),
    notes: m?.notes || "", recap: m?.recap || "", color: m?.color || "",
  });
  const [saving, setSaving] = useState(false);
  const isPast = form.date < todayIso;
  const set = (k) => (e) => setForm((f) => ({ ...f, [k]: e.target.value }));

  const save = async (e) => {
    e.preventDefault();
    if (!form.title.trim()) return toast.error("Give the meeting a title");
    setSaving(true);
    const payload = {
      title: form.title.trim(), date: form.date, time: form.time,
      project_id: form.project_id || null,
      attendees: form.attendees.split(",").map((s) => s.trim()).filter(Boolean),
      notes: form.notes, color: form.color,
    };
    try {
      if (m) await api.patch(`/meetings/${m.id}`, { ...payload, recap: form.recap });
      else await api.post("/meetings", payload);
      toast.success(m ? "Meeting updated" : "Meeting scheduled");
      onChanged();
    } catch { toast.error("Could not save meeting"); }
    finally { setSaving(false); }
  };
  const del = async () => {
    try {
      await api.delete(`/meetings/${m.id}`);
      toast.success("Meeting deleted");
      onChanged();
    } catch { toast.error("Could not delete meeting"); }
  };

  return (
    <Dialog open onOpenChange={(v) => !v && onClose()}>
      <DialogContent className="max-w-md" data-testid="meeting-dialog">
        <DialogHeader><DialogTitle className="font-display">{m ? "Meeting details" : "Schedule a meeting"}</DialogTitle></DialogHeader>
        <form onSubmit={save} className="space-y-3">
          <div>
            <Label htmlFor="mt-title">Title</Label>
            <Input id="mt-title" value={form.title} onChange={set("title")} placeholder="e.g. Weekly status sync" className="mt-1.5" data-testid="meeting-title-input" />
          </div>
          <div className="grid grid-cols-2 gap-3">
            <div>
              <Label htmlFor="mt-date">Date</Label>
              <Input id="mt-date" type="date" value={form.date} onChange={set("date")} className="mt-1.5" data-testid="meeting-date-input" />
            </div>
            <div>
              <Label htmlFor="mt-time">Time</Label>
              <Input id="mt-time" type="time" value={form.time} onChange={set("time")} className="mt-1.5" data-testid="meeting-time-input" />
            </div>
          </div>
          <div>
            <Label htmlFor="mt-project">Project (optional)</Label>
            <select id="mt-project" value={form.project_id} onChange={set("project_id")}
              className="mt-1.5 w-full h-9 rounded-md border border-input bg-background px-3 text-sm" data-testid="meeting-project-select">
              <option value="">— No project —</option>
              {(projects || []).map((p) => <option key={p.id} value={p.id}>{p.name}</option>)}
            </select>
          </div>
          <div>
            <Label>Color <span className="text-muted-foreground font-normal">(tell meetings apart or flag priority)</span></Label>
            <div className="flex items-center gap-1.5 mt-1.5 flex-wrap" data-testid="meeting-color-picker">
              <button type="button" onClick={() => setForm((f) => ({ ...f, color: "" }))}
                className={`h-6 px-2 rounded-full border text-[10px] font-semibold transition-all ${!form.color ? "border-foreground text-foreground" : "border-border text-muted-foreground hover:border-foreground/50"}`}
                title="Automatic (blue; amber when a past meeting needs a recap)" data-testid="meeting-color-auto">
                Auto
              </button>
              {MEETING_COLORS.map(({ c, label }) => (
                <button key={c} type="button" onClick={() => setForm((f) => ({ ...f, color: c }))} title={label}
                  className={`h-6 w-6 rounded-full transition-all ${form.color === c ? "ring-2 ring-offset-2 ring-foreground scale-110" : "hover:scale-110"}`}
                  style={{ background: c }} data-testid={`meeting-color-${c.replace("#", "")}`} />
              ))}
            </div>
          </div>
          <div>
            <Label htmlFor="mt-att">Attendees (comma-separated)</Label>
            <Input id="mt-att" value={form.attendees} onChange={set("attendees")} placeholder="Sarah, Marcus, Raj" className="mt-1.5" data-testid="meeting-attendees-input" />
          </div>
          <div>
            <Label htmlFor="mt-notes">Agenda notes</Label>
            <Textarea id="mt-notes" value={form.notes} onChange={set("notes")} rows={2} placeholder="Topics to cover…" className="mt-1.5" data-testid="meeting-notes-input" />
          </div>
          {m && isPast && (
            <div className="rounded-lg border border-amber-200 bg-amber-50/60 p-2.5">
              <Label htmlFor="mt-recap" className="text-amber-800 inline-flex items-center gap-1"><Clock className="h-3 w-3" /> Meeting recap</Label>
              <Textarea id="mt-recap" value={form.recap} onChange={set("recap")} rows={3} placeholder="Decisions made, next steps, follow-ups…" className="mt-1.5 bg-white" data-testid="meeting-recap-input" />
            </div>
          )}
          <div className="flex items-center gap-2 pt-1">
            {m && (
              <Button type="button" variant="ghost" size="sm" onClick={del} className="text-red-600 hover:text-red-700 hover:bg-red-50 gap-1" data-testid="meeting-delete-btn">
                <Trash2 className="h-3.5 w-3.5" /> Delete
              </Button>
            )}
            {form.project_id && (
              <Button type="button" variant="outline" size="sm" onClick={() => nav(`/projects/${form.project_id}?tab=meeting`)} className="gap-1" data-testid="meeting-ai-prep-btn">
                <Sparkles className="h-3.5 w-3.5" /> Prep with AI
              </Button>
            )}
            <div className="flex-1" />
            <Button type="submit" size="sm" disabled={saving} data-testid="meeting-save-btn">{saving ? "Saving…" : m ? "Save" : "Schedule"}</Button>
          </div>
        </form>
      </DialogContent>
    </Dialog>
  );
}
